import _regeneratorRuntime from "@babel/runtime/regenerator";
import _asyncToGenerator from "@babel/runtime/helpers/esm/asyncToGenerator";
import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _inherits from "@babel/runtime/helpers/esm/inherits";
import _possibleConstructorReturn from "@babel/runtime/helpers/esm/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/esm/getPrototypeOf";

function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function _createSuperInternal() { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }

function _isNativeReflectConstruct() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Date.prototype.toString.call(Reflect.construct(Date, [], function () {})); return true; } catch (e) { return false; } }

import { Subprovider } from '@bitski/provider-engine';
import { v4 as uuidv4 } from 'uuid';
import { DEFAULT_AUTHORIZED_METHODS as DEFAULT_SIGNATURE_METHODS } from '../constants';
import { SignerError } from '../errors/signer-error';
export var TransactionKind;

(function (TransactionKind) {
  TransactionKind["SendTransaction"] = "ETH_SEND_TRANSACTION";
  TransactionKind["SignTransaction"] = "ETH_SIGN_TRANSACTION";
  TransactionKind["Sign"] = "ETH_SIGN";
  TransactionKind["SignTypedData"] = "ETH_SIGN_TYPED_DATA";
})(TransactionKind || (TransactionKind = {}));
/**
 * A Subprovider that manages the interface between JSON-RPC and Bitski's proprietary transaction signing flow.
 * This class is responsible for transforming the JSON-RPC request into a Transaction object that the Bitski signer understands.
 * Also responsible for submitting the transaction to the network, and converting the response back into an RPC response.
 *
 * Important: this class assumes the transaction has all the necessary fields populated. The TransactionValidatorSubprovider
 * should be placed in front of this subprovider.
 */


export var SignatureSubprovider = /*#__PURE__*/function (_Subprovider) {
  _inherits(SignatureSubprovider, _Subprovider);

  var _super = _createSuper(SignatureSubprovider);

  function SignatureSubprovider(network, signer, tokenProvider, signatureMethods) {
    var _this;

    _classCallCheck(this, SignatureSubprovider);

    _this = _super.call(this);
    _this.network = network;
    _this.tokenProvider = tokenProvider;
    _this.signer = signer;
    _this.signatureMethods = signatureMethods || DEFAULT_SIGNATURE_METHODS;
    return _this;
  }
  /**
   * Handle RPC request from engine (called by)
   * @param payload RPC request payload
   * @param next Callback to skip handling this request
   * @param end Completion handler
   */


  _createClass(SignatureSubprovider, [{
    key: "handleRequest",
    value: function handleRequest(payload, next, end) {
      if (this.requiresSignature(payload.method)) {
        this.handleSignatureRequest(payload, end);
        return;
      }

      next();
    }
    /**
     * Called when a payload is received that needs a signature
     * @param payload The JSON-RPC request
     * @param callback The callback to call when the request has been handled
     */

  }, {
    key: "handleSignatureRequest",
    value: function () {
      var _handleSignatureRequest = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(payload, callback) {
        var accessToken, transaction, signedResponse, result;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                _context.prev = 0;
                _context.next = 3;
                return this.tokenProvider.getAccessToken();

              case 3:
                accessToken = _context.sent;
                _context.next = 6;
                return this.createBitskiTransaction(payload);

              case 6:
                transaction = _context.sent;
                _context.next = 9;
                return this.signer.sign(transaction, accessToken);

              case 9:
                signedResponse = _context.sent;
                _context.next = 12;
                return this.sendIfNeeded(payload, signedResponse);

              case 12:
                result = _context.sent;
                // Call the callback with the result
                callback(undefined, result);
                _context.next = 19;
                break;

              case 16:
                _context.prev = 16;
                _context.t0 = _context["catch"](0);
                // Call with the error if any of the steps fail
                callback(_context.t0, undefined);

              case 19:
              case "end":
                return _context.stop();
            }
          }
        }, _callee, this, [[0, 16]]);
      }));

      function handleSignatureRequest(_x, _x2) {
        return _handleSignatureRequest.apply(this, arguments);
      }

      return handleSignatureRequest;
    }()
    /** Should this subprovider handle the request?
     * @param method The RPC method of the request
     */

  }, {
    key: "requiresSignature",
    value: function requiresSignature(method) {
      return this.signatureMethods.includes(method);
    }
    /**
     * This will forward transactions that should be automatically submitted to the network, otherwise
     * resolve with the original value.
     * @param payload The original request payload
     * @param signedData signed data for the transaction
     */

  }, {
    key: "sendIfNeeded",
    value: function sendIfNeeded(payload, signedData) {
      if (payload.method === 'eth_sendTransaction') {
        // Create a send raw transaction payload
        var sendPayload = {
          id: 0,
          jsonrpc: '2.0',
          method: 'eth_sendRawTransaction',
          params: [signedData]
        };
        return this.performRequest(sendPayload);
      }

      return Promise.resolve(signedData);
    }
    /**
     * Load the balance so the web approval dialog can give the user better context
     * @param payload Payload for the request
     */

  }, {
    key: "loadBalanceIfNeeded",
    value: function loadBalanceIfNeeded(payload) {
      // Only necessary if this is a transaction
      var isTransaction = payload.method === 'eth_sendTransaction' || payload.method === 'eth_signTransaction';
      var isCustomRPC = !this.network.rpcUrl.includes('api.bitski.com');

      if (isTransaction && isCustomRPC) {
        var transaction = payload.params[0];
        var balancePayload = {
          id: 0,
          jsonrpc: '2.0',
          method: 'eth_getBalance',
          params: [transaction.from, 'latest']
        };
        return this.performRequest(balancePayload);
      }

      return Promise.resolve();
    }
    /**
     * Responsible for creating the Transaction object from a given RPC payload
     * @param payload JSON-RPC payload to extract the values from
     */

  }, {
    key: "createBitskiTransaction",
    value: function () {
      var _createBitskiTransaction = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(payload) {
        var context, kind, extractedPayload, transaction;
        return _regeneratorRuntime.wrap(function _callee2$(_context2) {
          while (1) {
            switch (_context2.prev = _context2.next) {
              case 0:
                _context2.next = 2;
                return this.createContext(payload);

              case 2:
                context = _context2.sent;
                kind = this.kindForMethod(payload.method);
                extractedPayload = this.createPayload(payload);
                transaction = {
                  id: uuidv4(),
                  kind: kind,
                  payload: extractedPayload,
                  context: context
                };
                return _context2.abrupt("return", transaction);

              case 7:
              case "end":
                return _context2.stop();
            }
          }
        }, _callee2, this);
      }));

      function createBitskiTransaction(_x3) {
        return _createBitskiTransaction.apply(this, arguments);
      }

      return createBitskiTransaction;
    }()
  }, {
    key: "createContext",
    value: function () {
      var _createContext = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3(request) {
        var balance;
        return _regeneratorRuntime.wrap(function _callee3$(_context3) {
          while (1) {
            switch (_context3.prev = _context3.next) {
              case 0:
                _context3.t0 = request.method;
                _context3.next = _context3.t0 === 'eth_sendTransaction' ? 3 : _context3.t0 === 'eth_signTransaction' ? 3 : _context3.t0 === 'eth_signTypedData' ? 7 : _context3.t0 === 'eth_signTypedData_v3' ? 7 : 10;
                break;

              case 3:
                _context3.next = 5;
                return this.loadBalanceIfNeeded(request);

              case 5:
                balance = _context3.sent;
                return _context3.abrupt("return", {
                  chainId: this.network.chainId,
                  currentBalance: balance
                });

              case 7:
                if (!(request.params && request.params.length > 0)) {
                  _context3.next = 9;
                  break;
                }

                return _context3.abrupt("return", {
                  from: request.params[0]
                });

              case 9:
                throw SignerError.MissingFrom();

              case 10:
                return _context3.abrupt("return", {});

              case 11:
              case "end":
                return _context3.stop();
            }
          }
        }, _callee3, this);
      }));

      function createContext(_x4) {
        return _createContext.apply(this, arguments);
      }

      return createContext;
    }()
    /**
     * Responsible for creating the payload from a given RPC request
     * @param request JSON-RPC request to extract params from
     */

  }, {
    key: "createPayload",
    value: function createPayload(request) {
      switch (request.method) {
        case 'eth_sendTransaction':
        case 'eth_signTransaction':
          if (request.params && request.params.length > 0) {
            return request.params[0];
          } else {
            throw SignerError.MissingTransaction();
          }

        case 'eth_sign':
          if (request.params && request.params.length > 1) {
            return {
              from: request.params[0],
              message: request.params[1]
            };
          } else {
            throw SignerError.MissingMessage();
          }

        case 'personal_sign':
          if (request.params && request.params.length > 1) {
            return {
              from: request.params[1],
              message: request.params[0]
            };
          } else {
            throw SignerError.MissingMessage();
          }

        case 'eth_signTypedData':
        case 'eth_signTypedData_v3':
          if (request.params && request.params.length > 1) {
            return request.params[1];
          } else {
            throw SignerError.MissingTypedData();
          }

        default:
          throw SignerError.UnsupportedMethod();
      }
    }
    /**
     * Determines a BitskiTransaction.Kind value from a given RPC method name
     * @param method The JSON-RPC method being requested
     */

  }, {
    key: "kindForMethod",
    value: function kindForMethod(method) {
      switch (method) {
        case 'eth_sendTransaction':
        case 'eth_signTransaction':
          // Convert both *sign* and *send* methods into a sign transaction.
          // (we will forward the transaction locally if needed)
          return TransactionKind.SignTransaction;

        case 'eth_sign':
        case 'personal_sign':
          return TransactionKind.Sign;

        case 'eth_signTypedData':
        case 'eth_signTypedData_v3':
          return TransactionKind.SignTypedData;

        default:
          throw SignerError.UnsupportedMethod();
      }
    } // Wraps emitPayload in a promise

  }, {
    key: "performRequest",
    value: function performRequest(payload) {
      var _this2 = this;

      return new Promise(function (fulfill, reject) {
        _this2.emitPayload(payload, function (err, result) {
          if (err) {
            reject(err);
          } else {
            fulfill(result.result);
          }
        });
      });
    }
  }]);

  return SignatureSubprovider;
}(Subprovider);